/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

// @mui material components
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Grid from "@mui/material/Grid";
import Avatar from "@mui/material/Avatar";
import TableContainer from "@mui/material/TableContainer";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 PRO React examples
import { useTranslation } from "react-i18next";
import { APP_STATE } from "services/Define";
import { FetchData } from "services/Request";
import { useSelector } from "react-redux";
import * as Func from "services/Function";

const initAttr = {
  id: "",
  code: "",
  customer_id: "",
  customer_name: "",
  branch_id: "",
  name: "",
  vat_type: "NO",
  revise: "NO",
  remark: "",
  total: "",
  vat: "",
  nettotal: "",
  status: "edit",
  print_url: "",
  created_at: "",
  updated_at: "",
  date_quotation: "",
};

function QuotationOpen({ parent }) {
  const mod = "quotation";
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const [attr, setAttr] = useState(initAttr);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  const open = () => {
    setLoading(true);
    setAttr(initAttr);
    setList([]);

    FetchData("OPTIONS", `/${mod}/print`, app.token, {
      id: parent.id,
      lang: app.lang,
    }).then((result) => {
      if (result.status === "ok") {
        setAttr(result.data);
        setList(result.list);

        setLoading(false);
      }
    });
  };

  const imageDisplay = (item) => {
    return <Avatar alt={item.name} src={item.product_image} variant="rounded" />;
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  };

  const renderInfo = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <MDInput
            label="ชื่อโปรเจ็ค"
            value={attr.name}
            variant="outlined"
            multiline
            inputProps={{
              readOnly: true,
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label="ลูกค้า"
            value={attr.customer_name}
            variant="outlined"
            multiline
            inputProps={{
              readOnly: true,
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label="วันที่เสนอราคา"
            value={Func.dateDisplay(attr.date_quotation)}
            variant="outlined"
            multiline
            inputProps={{
              readOnly: true,
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label={t(`attr.remark`)}
            value={attr.remark}
            variant="outlined"
            multiline
            inputProps={{
              readOnly: true,
            }}
            fullWidth
          />
        </Grid>
      </Grid>
    );
  };

  const renderOpen = () => {
    return (
      <MDBox mb={0}>
        <Grid container mb={3} spacing={2}>
          <Grid item xs={12} sm={12} md={12} lg={3}>
            {renderInfo()}
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={9}>
            <MDBox sx={{ border: "1px solid #e2e2e2", borderRadius: 1 }}>
              <TableContainer sx={{ boxShadow: 0, minHeight: 500 }}>
                <Table aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">No.</TableCell>
                      <TableCell align="center">รูป</TableCell>
                      <TableCell>สินค้า</TableCell>
                      <TableCell align="right">เวลาผลิต</TableCell>
                      <TableCell align="right">ราคา</TableCell>
                      <TableCell align="right">จำนวน</TableCell>
                      <TableCell>หน่วย</TableCell>
                      <TableCell align="right">รวม</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {list.map((row, i) => (
                      <TableRow key={row.id}>
                        <TableCell align="center" width="100">
                          {i + 1}
                        </TableCell>
                        <TableCell width="40">{imageDisplay(row)}</TableCell>
                        <TableCell align="left">{row.name}</TableCell>
                        <TableCell align="right">{row.job_day} วัน</TableCell>
                        <TableCell align="right">{Func.numberDisplay(row.price)}</TableCell>
                        <TableCell align="right">{Func.numberDisplay(row.quantity)}</TableCell>
                        <TableCell align="left">{row.unit_name}</TableCell>
                        <TableCell align="right">{Func.numberDisplay(row.total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Table aria-label="summary table">
                  <TableBody>
                    {attr.vat_type === "NO" ? null : (
                      <TableRow>
                        <TableCell align="right">
                          <MDTypography variant="h5">มูลค่าสินค้า</MDTypography>
                        </TableCell>
                        <TableCell align="right" width="200">
                          <MDTypography variant="h5">{Func.numberDisplay(attr.total)}</MDTypography>
                        </TableCell>
                      </TableRow>
                    )}
                    {attr.vat_type === "NO" ? null : (
                      <TableRow>
                        <TableCell align="right">
                          <MDTypography variant="h5">Vat 7%</MDTypography>
                        </TableCell>
                        <TableCell align="right">
                          <MDTypography variant="h5">{Func.numberDisplay(attr.vat)}</MDTypography>
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow>
                      <TableCell align="right">
                        <MDTypography variant="h5">รวมทั้งสิ้น</MDTypography>
                      </TableCell>
                      <TableCell align="right" width="200">
                        <MDTypography variant="h5">
                          {Func.numberDisplay(attr.nettotal)}
                        </MDTypography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    open();
  }, [parent]);

  return (
    <div>
      {renderOpen()}
      {renderLoading()}
    </div>
  );
}

QuotationOpen.propTypes = {
  parent: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
};

export default QuotationOpen;
